import React from 'react';
import { useNavigate } from 'react-router-dom';
import { fmtDate as _fmtDate, fmtDateTime } from '../../utils/fmt';

const isSuperAdmin = () => {
  try {
    if (localStorage.getItem('is_superadmin') === 'true') return true;
    return !!JSON.parse(localStorage.getItem('user') || '{}').is_superadmin;
  } catch { return false; }
};

// ── Field type detection ──────────────────────────────────────────────────────

const STATUS_HEURISTIC = {
  active: '#22c55e', enabled: '#22c55e', approved: '#22c55e', completed: '#22c55e',
  'closed-won': '#22c55e', won: '#22c55e', success: '#22c55e', paid: '#22c55e',
  resolved: '#22c55e', closed: '#94a3b8',
  inactive: '#94a3b8', disabled: '#94a3b8', expired: '#94a3b8', archived: '#94a3b8',
  pending: '#f59e0b', draft: '#f59e0b', review: '#8b5cf6', processing: '#3b82f6',
  open: '#3b82f6', new: '#3b82f6', 'in progress': '#3b82f6', in_progress: '#3b82f6',
  sent: '#0ea5e9', delivered: '#22c55e', read: '#22c55e',
  failed: '#ef4444', lost: '#ef4444', 'closed-lost': '#ef4444', rejected: '#ef4444',
  cancelled: '#ef4444', error: '#ef4444', overdue: '#ef4444', critical: '#ef4444',
  high: '#ef4444', medium: '#f59e0b', low: '#22c55e',
  warning: '#f59e0b', at_risk: '#ef4444',
  platinum: '#6d28d9', gold: '#92400e', silver: '#475569', bronze: '#9a3412',
};

const statusColor = (val) => {
  if (!val) return '#94a3b8';
  return STATUS_HEURISTIC[String(val).toLowerCase().replace(/[ -]/g, '_')] || '#64748b';
};

const is = {
  status:  f => ['status','stage','state','outcome','result','phase','priority','tier','condition','severity'].some(k => (f||'').toLowerCase() === k || (f||'').toLowerCase().endsWith('_'+k)),
  date:    f => { const x = (f||'').toLowerCase(); return x.endsWith('_at') || x.endsWith('_date') || x.startsWith('date_') || x === 'date'; },
  amount:  f => ['value','amount','price','cost','revenue','salary','fee','budget','total','subtotal'].some(k => (f||'').toLowerCase().includes(k)),
  email:   f => (f||'').toLowerCase().includes('email'),
  phone:   f => (f||'').toLowerCase().includes('phone'),
  url:     f => { const x = (f||'').toLowerCase(); return x === 'url' || x === 'website' || x === 'link'; },
  score:   f => { const x = (f||'').toLowerCase(); return x.includes('score') || x.includes('probability') || x.includes('_rate') || x.includes('percent'); },
};

const fmtDate = (s) => (typeof s === 'string' && s.includes('T')) ? fmtDateTime(s) : _fmtDate(s);

const fmtKES = (n) => {
  const num = Number(n);
  if (!num || isNaN(num)) return null;
  return 'KES ' + num.toLocaleString('en-KE');
};

// ── Field renderer ────────────────────────────────────────────────────────────

const renderValue = (field, value) => {
  if (value === null || value === undefined || value === '') {
    return <span style={{ color: 'var(--text-muted)' }}>—</span>;
  }

  if (typeof value === 'boolean') {
    return (
      <span style={{
        display: 'inline-block', padding: '2px 10px', borderRadius: 99,
        fontSize: 11, fontWeight: 600,
        background: value ? '#f0fdf4' : '#fef2f2',
        color: value ? '#15803d' : '#b91c1c',
      }}>
        {value ? 'Yes' : 'No'}
      </span>
    );
  }

  if (typeof value === 'object') {
    const str = Array.isArray(value) ? value.join(', ') : JSON.stringify(value);
    return <span style={{ fontSize: 13, color: 'var(--text-sub)', fontStyle: 'italic' }}>{str || '—'}</span>;
  }

  const str = String(value);

  if (is.status(field)) {
    const color = statusColor(str);
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: '2px 10px', borderRadius: 99, whiteSpace: 'nowrap',
        background: color + '18', border: `1px solid ${color}40`,
        fontSize: 11, fontWeight: 600, color,
      }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: color, flexShrink: 0 }} />
        {str}
      </span>
    );
  }

  if (is.date(field)) {
    return <span style={{ fontSize: 14 }}>{fmtDate(str) || str}</span>;
  }

  if (is.amount(field) && !isNaN(Number(value)) && Number(value) !== 0) {
    return <span style={{ fontWeight: 600, color: 'var(--text-main)' }}>{fmtKES(value) || str}</span>;
  }

  if (is.email(field)) {
    return <a href={`mailto:${str}`} style={{ color: 'var(--color-primary)', textDecoration: 'none' }}>{str}</a>;
  }

  if (is.phone(field)) {
    return <a href={`tel:${str}`} style={{ color: 'var(--color-primary)', textDecoration: 'none' }}>{str}</a>;
  }

  if (is.url(field) || str.startsWith('http')) {
    return (
      <a href={str} target="_blank" rel="noopener noreferrer"
        style={{ color: 'var(--color-primary)', textDecoration: 'none', wordBreak: 'break-all' }}>
        {str}
      </a>
    );
  }

  if (is.score(field) && !isNaN(Number(value))) {
    const n = Number(value);
    const pct = Math.min(100, Math.max(0, n));
    const color = pct >= 75 ? '#22c55e' : pct >= 50 ? '#3b82f6' : pct >= 25 ? '#f59e0b' : '#ef4444';
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ flex: 1, maxWidth: 120, height: 6, borderRadius: 99, background: 'var(--border)', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 99 }} />
        </div>
        <span style={{ fontWeight: 700, color, fontSize: 13 }}>{n}</span>
      </div>
    );
  }

  if (str.length > 160) {
    return <span style={{ whiteSpace: 'pre-wrap', lineHeight: 1.7, fontSize: 14, color: 'var(--text-sub)' }}>{str}</span>;
  }

  return <span style={{ fontSize: 14, color: 'var(--text-main)' }}>{str}</span>;
};

// ── Main component ────────────────────────────────────────────────────────────

const EXCLUDE_FIELDS = ['tenant_id', 'created_by', 'updated_by', 'id', 'uuid'];

const SingleItemDetailTemplate = ({
  details,
  columns,
  entityName = 'Record',
  listPath,
  editPath,
  onDelete,
  loading = false,
}) => {
  const navigate = useNavigate();

  if (loading || !details) {
    return (
      <div className="card">
        <div className="card-body">
          {[70, 50, 85, 60, 75, 45].map((w, i) => (
            <div key={i} className="skeleton" style={{ height: 16, width: `${w}%`, borderRadius: 4, marginBottom: 14 }} />
          ))}
        </div>
      </div>
    );
  }

  const safeColumns = (columns || []).filter(col => {
    if (EXCLUDE_FIELDS.includes(col.field)) return false;
    const val = details[col.field];
    if (val !== null && val !== undefined && typeof val === 'object' && !Array.isArray(val)) return false;
    return true;
  });

  const created = details.created_at;
  const updated = details.updated_at;

  return (
    <div>
      {/* ── Page header ── */}
      <div className="page-header">
        <div className="page-header-left">
          {listPath && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-muted)', marginBottom: 6 }}>
              <button
                onClick={() => navigate(listPath)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-primary)', padding: 0, fontSize: 13 }}
              >
                {entityName} List
              </button>
              <span>›</span>
              <span>Details</span>
            </div>
          )}
          <h1 className="page-title">{entityName} Details</h1>
          {(created || updated) && (
            <p className="page-subtitle">
              {created && `Created ${fmtDate(created) || created}`}
              {created && updated && ' · '}
              {updated && `Updated ${fmtDate(updated) || updated}`}
            </p>
          )}
        </div>
        <div className="page-actions">
          {onDelete && isSuperAdmin() && (
            <button className="btn btn-danger" onClick={onDelete}>
              <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" style={{flexShrink:0}}><path d="M2 4h12M6 4V3a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v1M5 4v9a1 1 0 0 0 1 1h4a1 1 0 0 0 1-1V4"/></svg>
              Delete
            </button>
          )}
          {editPath && (
            <button className="btn btn-secondary" onClick={() => navigate(editPath)}>
              <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" style={{flexShrink:0}}><path d="M11.5 2.5l2 2L5 13H3v-2L11.5 2.5z"/></svg>
              Edit {entityName}
            </button>
          )}
          <button
            className="btn btn-ghost"
            onClick={() => listPath ? navigate(listPath) : window.history.back()}
          >
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" style={{flexShrink:0}}><path d="M10 3L5 8l5 5"/></svg>
            Back
          </button>
        </div>
      </div>

      {/* ── Fields card ── */}
      <div className="card">
        <div className="card-body">
          <div className="form-grid">
            {safeColumns.map(col => {
              const value = details[col.field];
              const isLong = typeof value === 'string' && value.length > 160;
              const isFullWidth = isLong || is.url(col.field) || (typeof value === 'string' && value.startsWith('http'));
              return (
                <div key={col.field} className={`form-field${isFullWidth ? ' span-full' : ''}`}>
                  <div className="form-label">{col.label}</div>
                  <div style={{
                    padding: '8px 12px', background: 'var(--surface)',
                    borderRadius: 6, border: '1px solid var(--border)',
                    minHeight: 38, display: 'flex', alignItems: 'center',
                  }}>
                    {renderValue(col.field, value)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SingleItemDetailTemplate;
